/**
 * Snap rough AI outlines (circles / coarse polygons) to the real tissue contour
 * visible in the capture screenshot the model was given.
 *
 * Vision LLMs locate structures but cannot trace borders pixel-accurately, so for
 * whole-fragment outlines we segment the screenshot (non-background pixels),
 * pick the connected component the AI shape covers, and replace the geometry
 * with that component's traced, simplified boundary — still in canvas_pixels.
 */

/** Max working raster edge; larger screenshots are downsampled for speed. */
const MAX_WORK_EDGE = 768;
/** Pixel counts as tissue when clearly coloured or clearly darker than glass. */
const SAT_THRESH = 14;
const BG_DIFF = 10;
const BASO_LUM = 170;
const INK_LUM = 60;
/** Pale-stain epidermis: min purple saturation and how much darker than glass it must be. */
const PALE_EPI_MIN_SAT = 22;
const PALE_EPI_MIN_DARKER = 15;
const PALE_EPI_SAT_FRAC = 0.3;
/** Hematoxylin tint: bluer than red by at least this much. */
const PALE_EPI_MIN_BLUE = 8;
/** Pale-stain rule only when typical tissue saturation is at most this (faded eosin). */
const PALE_STAIN_MAX_MED_SAT = 30;
/** Pure-white "no data" outside the scanned area (all channels at or above this). */
const VOID_MIN = 250;
/** Heuristic snap (no explicit flag): AI shape must cover most of one fragment. */
const AUTO_MIN_COVERAGE = 0.5;
const AUTO_MIN_PRECISION = 0.25;
/** Ignore specks smaller than this fraction of the raster. */
const MIN_COMPONENT_FRAC = 0.002;
const MAX_CONTOUR_POINTS = 400;

const SNAP_CLASSES = new Set(["tissue", "fragment", "specimen", "tissue_fragment"]);
/** Structures inside a fragment: never auto-snapped to the whole fragment (only clipped). */
const INTERNAL_CLASSES = new Set([
	"epidermis", "papillary_dermis", "reticular_dermis", "dej", "sej", "follicle", "hair_follicle",
	"adnexal", "sebaceous", "vessel", "inflammation", "ulcer", "tumour", "surgical_ink", "ink",
	"closer_look", "roi", "measurement",
]);

/** Explicit request: properties.snap === "tissue" or a whole-fragment class. */
export const featureWantsTissueSnap = (feature) => {
	const p = feature?.properties || {};
	if (p.snap === false || p.snap === "none") return false;
	if (p.snap === true || p.snap === "tissue") return true;
	const cls = String(p.class || "").toLowerCase();
	return SNAP_CLASSES.has(cls);
};

const _loadImage = (src) => new Promise((resolve, reject) => {
	const img = new Image();
	img.crossOrigin = "anonymous";
	img.onload = () => resolve(img);
	img.onerror = reject;
	img.src = src;
});

/**
 * Build tissue mask + connected-component labels for a screenshot.
 * Coordinates of the returned raster relate to canvas pixels via `scale`
 * (canvasPx = rasterPx * scale).
 */
export const buildTissueRaster = async (src, canvasSize) => {
	const img = await _loadImage(src);
	const natW = img.naturalWidth || img.width;
	const natH = img.naturalHeight || img.height;
	if (!(natW > 4 && natH > 4)) return null;
	const cw = Number(canvasSize?.[0]) || natW;
	const ch = Number(canvasSize?.[1]) || natH;
	const shrink = Math.min(1, MAX_WORK_EDGE / Math.max(cw, ch));
	const w = Math.max(4, Math.round(cw * shrink));
	const h = Math.max(4, Math.round(ch * shrink));

	const canvas = document.createElement("canvas");
	canvas.width = w;
	canvas.height = h;
	const ctx = canvas.getContext("2d", {willReadFrequently: true});
	ctx.drawImage(img, 0, 0, w, h);
	return rasterFromPixels(ctx.getImageData(0, 0, w, h).data, w, h, 1 / shrink);
};

/** Segment RGBA pixels (already at working size); canvasPx = rasterPx * scale. */
export const rasterFromPixels = (data, w, h, scale) => {
	// Background = median colour of the brightest ~20% pixels (white or grey glass).
	const lums = new Float32Array(w * h);
	for (let i = 0, p = 0; p < w * h; p += 1, i += 4) {
		lums[p] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
	}
	const median = (arr) => (arr.length ? arr.sort((a, b) => a - b)[arr.length >> 1] : 255);
	const estimateBg = (include) => {
		const sub = [];
		for (let p = 0; p < w * h; p += 1) if (include(p)) sub.push(lums[p]);
		if (!sub.length) return null;
		const lumCut = Float32Array.from(sub).sort()[Math.floor(sub.length * 0.8)];
		const bgR = [];
		const bgG = [];
		const bgB = [];
		for (let i = 0, p = 0; p < w * h; p += 7, i += 28) {
			if (include(p) && lums[p] >= lumCut) {
				bgR.push(data[i]);
				bgG.push(data[i + 1]);
				bgB.push(data[i + 2]);
			}
		}
		return [median(bgR), median(bgG), median(bgB)];
	};
	const isVoid = (p) => data[p * 4] >= VOID_MIN && data[p * 4 + 1] >= VOID_MIN && data[p * 4 + 2] >= VOID_MIN;
	const nearBg = (p, c) => Math.max(
		Math.abs(data[p * 4] - c[0]), Math.abs(data[p * 4 + 1] - c[1]), Math.abs(data[p * 4 + 2] - c[2]),
	) < BG_DIFF;
	let bg = estimateBg(() => true);
	// Low-mag captures: the unscanned area renders pure white around the darker scanned
	// glass. If the non-white pixels carry their own glass level, use it and treat the
	// white as no-data — otherwise the whole scan rectangle segments as one "fragment".
	let voidMask = null;
	let voidN = 0;
	for (let p = 0; p < w * h; p += 1) if (isVoid(p)) voidN += 1;
	if (voidN > w * h * 0.05 && voidN < w * h * 0.95) {
		const glass = estimateBg((p) => !isVoid(p));
		const glassSat = glass ? Math.max(...glass) - Math.min(...glass) : 255;
		let near = 0;
		for (let p = 0; p < w * h; p += 1) if (!isVoid(p) && nearBg(p, glass)) near += 1;
		if (glass && glassSat < SAT_THRESH && near >= (w * h - voidN) * 0.25) {
			bg = glass;
			voidMask = new Uint8Array(w * h);
			for (let p = 0; p < w * h; p += 1) if (isVoid(p)) voidMask[p] = 1;
		}
	}

	let mask = new Uint8Array(w * h);
	let baso = new Uint8Array(w * h);
	const sats = new Uint8Array(w * h);
	for (let i = 0, p = 0; p < w * h; p += 1, i += 4) {
		const r = data[i];
		const g = data[i + 1];
		const b = data[i + 2];
		if (data[i + 3] < 10 || (voidMask && voidMask[p])) continue;
		const sat = Math.max(r, g, b) - Math.min(r, g, b);
		sats[p] = sat;
		const diff = Math.max(Math.abs(r - bg[0]), Math.abs(g - bg[1]), Math.abs(b - bg[2]));
		if (sat >= SAT_THRESH || diff >= BG_DIFF) mask[p] = 1;
		// Hematoxylin-dense (purple, darker than stroma; not black ink).
		if (lums[p] < BASO_LUM && lums[p] > INK_LUM && b >= g + 10) baso[p] = 1;
	}
	// Pale / faded stains: epidermis never gets below BASO_LUM but is still far more
	// saturated purple than the stroma. Use a cut relative to this capture's tissue.
	const bgLum = 0.299 * bg[0] + 0.587 * bg[1] + 0.114 * bg[2];
	const tissueSats = [];
	for (let p = 0; p < w * h; p += 3) if (mask[p]) tissueSats.push(sats[p]);
	tissueSats.sort((a, b) => a - b);
	const satMed = tissueSats.length ? tissueSats[tissueSats.length >> 1] : 255;
	// Only for faded stains (pale stroma). On vivid slides the magenta dermis is as saturated
	// as the epidermis and the dark rule above already finds the basal layer.
	if (tissueSats.length > 50 && satMed <= PALE_STAIN_MAX_MED_SAT) {
		// Between typical stroma (median) and the most saturated tissue (p97): keeps the paler
		// basal layer in the band without pulling in the dermis.
		const satHi = tissueSats[Math.floor(tissueSats.length * 0.97)];
		const satCut = Math.max(PALE_EPI_MIN_SAT, satMed + PALE_EPI_SAT_FRAC * (satHi - satMed));
		for (let i = 0, p = 0; p < w * h; p += 1, i += 4) {
			if (baso[p] || !mask[p]) continue;
			const r = data[i];
			const g = data[i + 1];
			const b = data[i + 2];
			if (sats[p] >= satCut && b >= g + 10 && b >= r + PALE_EPI_MIN_BLUE && lums[p] > INK_LUM && lums[p] < bgLum - PALE_EPI_MIN_DARKER) {
				baso[p] = 1;
			}
		}
	}
	// Before gap closing: lets _splitSharedComponents tell touching fragments apart.
	const rawMask = Uint8Array.from(mask);
	// Close small gaps (pale stroma / tears) so a fragment stays one component.
	const rad = Math.max(1, Math.round(Math.max(w, h) / 192));
	mask = _erode(_dilate(mask, w, h, rad), w, h, rad);
	// Light open to drop dust specks.
	mask = _dilate(_erode(mask, w, h, 1), w, h, 1);
	_fillHoles(mask, w, h);

	const {labels, areas} = _labelComponents(mask, w, h);

	// Surface epithelium candidates: basophilic components that reach the tissue surface.
	const bRad = Math.max(1, Math.round(Math.max(w, h) / 256));
	baso = _erode(_dilate(baso, w, h, bRad), w, h, bRad);
	for (let i = 0; i < baso.length; i += 1) if (!mask[i]) baso[i] = 0;
	const dist = _distanceToGlass(mask, w, h);
	const bl = _labelComponents(baso, w, h);
	const surfaceTol = Math.max(3, bRad * 3);
	const atSurface = new Uint8Array(bl.areas.length);
	for (let i = 0; i < baso.length; i += 1) {
		if (baso[i] && dist[i] <= surfaceTol) atSurface[bl.labels[i]] = 1;
	}
	const epi = new Uint8Array(w * h);
	for (let i = 0; i < baso.length; i += 1) if (baso[i] && atSurface[bl.labels[i]]) epi[i] = 1;

	return {w, h, scale, mask, rawMask, labels, areas, epi, dist};
};

/** 4-neighbour BFS distance (px) from glass; capped for speed. */
const _distanceToGlass = (mask, w, h, cap = 4096) => {
	const dist = new Int32Array(w * h).fill(cap);
	const queue = new Int32Array(w * h);
	let head = 0;
	let tail = 0;
	for (let i = 0; i < mask.length; i += 1) {
		if (!mask[i]) {
			dist[i] = 0;
			queue[tail++] = i;
		}
	}
	while (head < tail) {
		const p = queue[head++];
		const d = dist[p] + 1;
		const x = p % w;
		if (x > 0 && dist[p - 1] > d) { dist[p - 1] = d; queue[tail++] = p - 1; }
		if (x < w - 1 && dist[p + 1] > d) { dist[p + 1] = d; queue[tail++] = p + 1; }
		if (p >= w && dist[p - w] > d) { dist[p - w] = d; queue[tail++] = p - w; }
		if (p < w * (h - 1) && dist[p + w] > d) { dist[p + w] = d; queue[tail++] = p + w; }
	}
	return dist;
};

const _dilate = (src, w, h, r) => {
	// Separable square structuring element.
	const tmp = new Uint8Array(w * h);
	const out = new Uint8Array(w * h);
	for (let y = 0; y < h; y += 1) {
		for (let x = 0; x < w; x += 1) {
			let v = 0;
			for (let k = Math.max(0, x - r); k <= Math.min(w - 1, x + r) && !v; k += 1) v = src[y * w + k];
			tmp[y * w + x] = v;
		}
	}
	for (let y = 0; y < h; y += 1) {
		for (let x = 0; x < w; x += 1) {
			let v = 0;
			for (let k = Math.max(0, y - r); k <= Math.min(h - 1, y + r) && !v; k += 1) v = tmp[k * w + x];
			out[y * w + x] = v;
		}
	}
	return out;
};

const _erode = (src, w, h, r) => {
	const inv = new Uint8Array(w * h);
	for (let i = 0; i < inv.length; i += 1) inv[i] = src[i] ? 0 : 1;
	const d = _dilate(inv, w, h, r);
	for (let i = 0; i < d.length; i += 1) d[i] = d[i] ? 0 : 1;
	return d;
};

/** Background reachable from the border stays 0; enclosed holes become tissue. */
const _fillHoles = (mask, w, h) => {
	const seen = new Uint8Array(w * h);
	const stack = [];
	const seed = (p) => {
		if (!mask[p] && !seen[p]) {
			seen[p] = 1;
			stack.push(p);
		}
	};
	for (let x = 0; x < w; x += 1) {
		seed(x);
		seed((h - 1) * w + x);
	}
	for (let y = 0; y < h; y += 1) {
		seed(y * w);
		seed(y * w + w - 1);
	}
	while (stack.length) {
		const p = stack.pop();
		const x = p % w;
		const y = (p - x) / w;
		if (x > 0) seed(p - 1);
		if (x < w - 1) seed(p + 1);
		if (y > 0) seed(p - w);
		if (y < h - 1) seed(p + w);
	}
	for (let i = 0; i < mask.length; i += 1) if (!seen[i]) mask[i] = 1;
};

const _labelComponents = (mask, w, h) => {
	const labels = new Int32Array(w * h);
	const areas = [0];
	let next = 1;
	const stack = [];
	for (let s = 0; s < mask.length; s += 1) {
		if (!mask[s] || labels[s]) continue;
		let area = 0;
		labels[s] = next;
		stack.push(s);
		while (stack.length) {
			const p = stack.pop();
			area += 1;
			const x = p % w;
			const y = (p - x) / w;
			const nb = [
				x > 0 ? p - 1 : -1,
				x < w - 1 ? p + 1 : -1,
				y > 0 ? p - w : -1,
				y < h - 1 ? p + w : -1,
			];
			for (const q of nb) {
				if (q >= 0 && mask[q] && !labels[q]) {
					labels[q] = next;
					stack.push(q);
				}
			}
		}
		areas.push(area);
		next += 1;
	}
	return {labels, areas};
};

/** Rasterize an AI feature (Polygon / Circle point) into raster coords. */
const _featureFootprint = (feature, raster) => {
	const geom = feature?.geometry;
	const {w, h, scale} = raster;
	const fp = new Uint8Array(w * h);
	if (!geom) return null;
	if (geom.type === "Point" && Number(feature.properties?.radius) > 0) {
		const cx = Number(geom.coordinates[0]) / scale;
		const cy = Number(geom.coordinates[1]) / scale;
		const r = Number(feature.properties.radius) / scale;
		for (let y = Math.max(0, Math.floor(cy - r)); y <= Math.min(h - 1, Math.ceil(cy + r)); y += 1) {
			for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(w - 1, Math.ceil(cx + r)); x += 1) {
				if ((x - cx) ** 2 + (y - cy) ** 2 <= r * r) fp[y * w + x] = 1;
			}
		}
		return fp;
	}
	if (geom.type === "Polygon" && Array.isArray(geom.coordinates?.[0])) {
		const ring = geom.coordinates[0].map((c) => [Number(c[0]) / scale, Number(c[1]) / scale]);
		if (ring.length < 3) return null;
		// Even-odd scanline fill.
		for (let y = 0; y < h; y += 1) {
			const yc = y + 0.5;
			const xs = [];
			for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
				const [xi, yi] = ring[i];
				const [xj, yj] = ring[j];
				if ((yi > yc) !== (yj > yc)) xs.push(xi + ((yc - yi) / (yj - yi)) * (xj - xi));
			}
			xs.sort((a, b) => a - b);
			for (let k = 0; k + 1 < xs.length; k += 2) {
				const x0 = Math.max(0, Math.ceil(xs[k] - 0.5));
				const x1 = Math.min(w - 1, Math.floor(xs[k + 1] - 0.5));
				for (let x = x0; x <= x1; x += 1) fp[y * w + x] = 1;
			}
		}
		return fp;
	}
	return null;
};

/** Overlap (pixels) of footprint with each component label. */
const _overlapByLabel = (fp, raster) => {
	const counts = new Map();
	let fpArea = 0;
	for (let i = 0; i < fp.length; i += 1) {
		if (!fp[i]) continue;
		fpArea += 1;
		const l = raster.labels[i];
		if (l) counts.set(l, (counts.get(l) || 0) + 1);
	}
	return {counts, fpArea};
};

/** Moore-neighbour boundary trace of one component; returns raster-space ring. */
const _traceComponent = (raster, label) => {
	const {w, h, labels} = raster;
	const inC = (x, y) => x >= 0 && y >= 0 && x < w && y < h && labels[y * w + x] === label;
	let start = -1;
	for (let i = 0; i < labels.length; i += 1) {
		if (labels[i] === label) {
			start = i;
			break;
		}
	}
	if (start < 0) return null;
	const sx = start % w;
	const sy = (start - sx) / w;
	// Clockwise neighbours starting West.
	const dirs = [[-1, 0], [-1, -1], [0, -1], [1, -1], [1, 0], [1, 1], [0, 1], [-1, 1]];
	const ring = [[sx, sy]];
	let cx = sx;
	let cy = sy;
	let back = 0; // entered start scanning from the west (known background)
	const limit = w * h * 4;
	for (let step = 0; step < limit; step += 1) {
		let found = false;
		for (let k = 1; k <= 8; k += 1) {
			const d = (back + k) % 8;
			const nx = cx + dirs[d][0];
			const ny = cy + dirs[d][1];
			if (inC(nx, ny)) {
				cx = nx;
				cy = ny;
				back = (d + 4) % 8;
				found = true;
				break;
			}
		}
		if (!found) break; // isolated pixel
		if (cx === sx && cy === sy) break;
		ring.push([cx, cy]);
	}
	return ring.length >= 3 ? ring : null;
};

const _perpDist = (p, a, b) => {
	const dx = b[0] - a[0];
	const dy = b[1] - a[1];
	const len2 = dx * dx + dy * dy;
	if (!len2) return Math.hypot(p[0] - a[0], p[1] - a[1]);
	return Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]) / Math.sqrt(len2);
};

const _douglasPeucker = (pts, eps) => {
	if (pts.length < 3) return pts;
	const keep = new Uint8Array(pts.length);
	keep[0] = 1;
	keep[pts.length - 1] = 1;
	const stack = [[0, pts.length - 1]];
	while (stack.length) {
		const [s, e] = stack.pop();
		let maxD = 0;
		let idx = -1;
		for (let i = s + 1; i < e; i += 1) {
			const d = _perpDist(pts[i], pts[s], pts[e]);
			if (d > maxD) {
				maxD = d;
				idx = i;
			}
		}
		if (idx > 0 && maxD > eps) {
			keep[idx] = 1;
			stack.push([s, idx], [idx, e]);
		}
	}
	return pts.filter((_, i) => keep[i]);
};

const _simplifyRing = (ring, maxPoints) => {
	let eps = 1.0;
	let out = _douglasPeucker(ring, eps);
	while (out.length > maxPoints && eps < 20) {
		eps *= 1.5;
		out = _douglasPeucker(ring, eps);
	}
	return out;
};

/**
 * Snap a batch of features from one capture site. Features that do not qualify
 * (lines, small structures inside a fragment, no tissue match) pass through.
 * Each tissue fragment is claimed by at most one feature (best overlap wins).
 */
/**
 * Fragments that nearly touch (low mag) merge into one blob after gap closing. When two or
 * more explicit fragment outlines sit mostly on the same blob, the model is telling us they
 * are separate pieces: split the blob between them, each pixel going to the geodesically
 * nearest outline.
 */
const _splitSharedComponents = (features, raster, minArea) => {
	const {w, h, labels, areas} = raster;
	const groups = new Map(); // label -> [{fi, fp}]
	features.forEach((feature, fi) => {
		const geom = feature?.geometry;
		if (!geom || (geom.type !== "Polygon" && geom.type !== "Point") || !featureWantsTissueSnap(feature)) return;
		const fp = _featureFootprint(feature, raster);
		if (!fp) return;
		const {counts} = _overlapByLabel(fp, raster);
		let best = 0;
		let bestOv = 0;
		counts.forEach((ov, label) => {
			if (areas[label] >= minArea && ov > bestOv) {
				best = label;
				bestOv = ov;
			}
		});
		if (!best) return;
		if (!groups.has(best)) groups.set(best, []);
		groups.get(best).push({fi, fp});
	});
	let newLabels = null;
	let newAreas = null;
	groups.forEach((group, label) => {
		if (group.length < 2) return;
		if (!newLabels) {
			newLabels = Int32Array.from(labels);
			newAreas = Array.from(areas);
		}
		const owner = new Int32Array(w * h).fill(-1);
		const queue = new Int32Array(w * h);
		let head = 0;
		let tail = 0;
		// Preferred seeds: whole pre-closing pieces, each given to the outline overlapping it most
		// (a rough outline may spill onto the neighbour, but rarely covers more of it).
		if (raster.rawMask) {
			const sub = new Uint8Array(w * h);
			for (let p = 0; p < labels.length; p += 1) if (labels[p] === label && raster.rawMask[p]) sub[p] = 1;
			const pieces = _labelComponents(sub, w, h);
			const ov = pieces.areas.map(() => new Array(group.length).fill(0));
			for (let p = 0; p < sub.length; p += 1) {
				const l = pieces.labels[p];
				if (!l) continue;
				for (let k = 0; k < group.length; k += 1) if (group[k].fp[p]) ov[l][k] += 1;
			}
			const pieceOwner = ov.map((row) => {
				let best = -1;
				for (let k = 0; k < row.length; k += 1) if (row[k] > 0 && (best < 0 || row[k] > row[best])) best = k;
				return best;
			});
			if (new Set(pieceOwner.filter((k) => k >= 0)).size >= 2) {
				for (let p = 0; p < sub.length; p += 1) {
					const k = pieceOwner[pieces.labels[p]];
					if (pieces.labels[p] && k >= 0) {
						owner[p] = k;
						queue[tail++] = p;
					}
				}
			}
		}
		// Fallback (pieces really touch): seed from the outlines themselves.
		const fromOutlines = !tail;
		for (let p = 0; fromOutlines && p < labels.length; p += 1) {
			if (labels[p] !== label) continue;
			let only = -1;
			for (let k = 0; k < group.length; k += 1) {
				if (!group[k].fp[p]) continue;
				only = only === -1 ? k : -2; // pixel inside two outlines seeds neither
			}
			if (only >= 0) {
				owner[p] = only;
				queue[tail++] = p;
			}
		}
		while (head < tail) {
			const p = queue[head++];
			const x = p % w;
			const nb = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, p >= w ? p - w : -1, p < w * (h - 1) ? p + w : -1];
			for (const q of nb) {
				if (q >= 0 && labels[q] === label && owner[q] === -1) {
					owner[q] = owner[p];
					queue[tail++] = q;
				}
			}
		}
		const ids = group.map((_, k) => (k === 0 ? label : newAreas.push(0) - 1));
		newAreas[label] = 0;
		for (let p = 0; p < labels.length; p += 1) {
			if (labels[p] !== label) continue;
			const id = owner[p] >= 0 ? ids[owner[p]] : label;
			newLabels[p] = id;
			newAreas[id] += 1;
		}
	});
	return newLabels ? {...raster, labels: newLabels, areas: newAreas} : raster;
};

export const snapFeaturesToTissue = (features, raster) => {
	if (!raster || !features?.length) return features;
	const minArea = raster.w * raster.h * MIN_COMPONENT_FRAC;
	raster = _splitSharedComponents(features, raster, minArea);
	const candidates = [];
	features.forEach((feature, fi) => {
		const geom = feature?.geometry;
		if (!geom || (geom.type !== "Polygon" && geom.type !== "Point")) return;
		const explicit = featureWantsTissueSnap(feature);
		const cls = String(feature?.properties?.class || "").toLowerCase().replace(/_\d+$/, "");
		if (!explicit && INTERNAL_CLASSES.has(cls)) return;
		if (feature?.properties?.snap === false || feature?.properties?.snap === "none") return;
		const fp = _featureFootprint(feature, raster);
		if (!fp) return;
		const {counts, fpArea} = _overlapByLabel(fp, raster);
		if (!fpArea) return;
		counts.forEach((ov, label) => {
			const area = raster.areas[label];
			if (area < minArea) return;
			const coverage = ov / area; // how much of the fragment the AI shape covers
			const precision = ov / fpArea; // how much of the AI shape is that fragment
			if (!explicit && (coverage < AUTO_MIN_COVERAGE || precision < AUTO_MIN_PRECISION)) return;
			candidates.push({fi, label, ov, score: coverage + precision});
		});
	});
	candidates.sort((a, b) => b.score - a.score);
	const assigned = new Map();
	const usedLabels = new Set();
	for (const c of candidates) {
		if (assigned.has(c.fi) || usedLabels.has(c.label)) continue;
		assigned.set(c.fi, c.label);
		usedLabels.add(c.label);
	}
	return features.map((feature, fi) => {
		if (!assigned.has(fi)) {
			const cls = _featureClass(feature);
			if (feature?.properties?.snap === false || feature?.properties?.snap === "none") return feature;
			if (raster.epi && cls === "epidermis") return _snapEpidermis(feature, raster) || _clipRegionToTissue(feature, raster);
			if (raster.epi && (cls === "dej" || cls === "sej")) {
				if (String(feature?.properties?.tool || "").toLowerCase() === "arrow") {
					return _snapArrowTipToJunction(feature, raster) || feature;
				}
				return _snapJunction(feature, raster) || feature;
			}
			return _clipRegionToTissue(feature, raster);
		}
		const ring = _traceComponent(raster, assigned.get(fi));
		if (!ring) return feature;
		const simple = _ringToCanvas(ring, raster);
		if (simple.length < 4) return feature;
		const props = {...(feature.properties || {}), tool: "Freehand", snapped: "tissue"};
		delete props.radius;
		return {...feature, geometry: {type: "Polygon", coordinates: [simple]}, properties: props};
	});
};

const _featureClass = (feature) => {
	const p = feature?.properties || {};
	return String(p.class || p.label || "").toLowerCase().replace(/_\d+$/, "");
};

/** Rasterize a polyline with a given half-width (raster px). */
const _lineFootprint = (coords, raster, halfWidth) => {
	const {w, h, scale} = raster;
	const fp = new Uint8Array(w * h);
	const pts = (coords || []).map((c) => [Number(c[0]) / scale, Number(c[1]) / scale]);
	for (let i = 0; i + 1 < pts.length; i += 1) {
		const [x0, y0] = pts[i];
		const [x1, y1] = pts[i + 1];
		const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0)));
		for (let k = 0; k <= steps; k += 1) {
			const cx = Math.round(x0 + ((x1 - x0) * k) / steps);
			const cy = Math.round(y0 + ((y1 - y0) * k) / steps);
			if (cx >= 0 && cy >= 0 && cx < w && cy < h) fp[cy * w + cx] = 1;
		}
	}
	return halfWidth > 0 ? _dilate(fp, w, h, halfWidth) : fp;
};

/**
 * Epidermis: the model finds roughly the right stretch of surface but cannot trace a
 * thin band. Take the surface basophilic band inside a corridor around its shape.
 */
const _snapEpidermis = (feature, raster) => {
	const {w, h} = raster;
	const geom = feature?.geometry;
	const corridor = Math.max(3, Math.round(Math.max(w, h) * 0.04));
	let fp = null;
	if (geom?.type === "LineString") fp = _lineFootprint(geom.coordinates, raster, corridor);
	else {
		fp = _featureFootprint(feature, raster);
		if (fp) fp = _dilate(fp, w, h, corridor);
	}
	if (!fp) return null;
	let inter = new Uint8Array(fp.length);
	let n = 0;
	for (let i = 0; i < fp.length; i += 1) {
		if (fp[i] && raster.epi[i]) {
			inter[i] = 1;
			n += 1;
		}
	}
	if (n < w * h * 0.0004) return null;
	// Bridge small breaks where the band thins, then keep the main stretch.
	const gap = Math.max(1, Math.round(Math.max(w, h) / 160));
	inter = _erode(_dilate(inter, w, h, gap), w, h, gap);
	const {labels, areas} = _labelComponents(inter, w, h);
	let best = 0;
	for (let l = 1; l < areas.length; l += 1) if (!best || areas[l] > areas[best]) best = l;
	if (!best) return null;
	const ring = _traceComponent({w, h, labels}, best);
	if (!ring) return null;
	const simple = _ringToCanvas(ring, raster);
	if (simple.length < 4) return null;
	const props = {...(feature.properties || {}), tool: "Freehand", snapped: "epidermis"};
	delete props.radius;
	return {...feature, geometry: {type: "Polygon", coordinates: [simple]}, properties: props};
};

/** Inner edge of the surface band = band pixels whose non-band neighbour lies deeper in the tissue. */
const _junctionEdge = (raster) => {
	const {w, h, mask, dist} = raster;
	// Pale pockets inside the band are not the junction.
	const epi = Uint8Array.from(raster.epi);
	_fillHoles(epi, w, h);
	const inner = new Uint8Array(epi.length);
	let any = 0;
	for (let p = 0; p < epi.length; p += 1) {
		if (!epi[p]) continue;
		const x = p % w;
		const nb = [x > 0 ? p - 1 : -1, x < w - 1 ? p + 1 : -1, p - w, p + w];
		for (const q of nb) {
			if (q >= 0 && q < epi.length && mask[q] && !epi[q] && dist[q] > dist[p]) {
				inner[p] = 1;
				any += 1;
				break;
			}
		}
	}
	return any ? inner : null;
};

const _junctionRadius = (raster) => Math.max(4, Math.round(Math.max(raster.w, raster.h) * 0.06));

/**
 * Arrow pointing at the DEJ / SEJ: move only the tip onto the junction; the tail stays
 * where the model put it so the shaft keeps its length and direction of approach.
 */
const _snapArrowTipToJunction = (feature, raster) => {
	const coords = feature?.geometry?.coordinates;
	if (feature?.geometry?.type !== "LineString" || !Array.isArray(coords) || coords.length < 2) return null;
	const inner = _junctionEdge(raster);
	if (!inner) return null;
	const {w, h, scale} = raster;
	const R = _junctionRadius(raster);
	const tip = coords[coords.length - 1];
	const px = Number(tip[0]) / scale;
	const py = Number(tip[1]) / scale;
	let bestD = Infinity;
	let bx = -1;
	let by = -1;
	for (let y = Math.max(0, Math.round(py) - R); y <= Math.min(h - 1, Math.round(py) + R); y += 1) {
		for (let x = Math.max(0, Math.round(px) - R); x <= Math.min(w - 1, Math.round(px) + R); x += 1) {
			if (!inner[y * w + x]) continue;
			const d = (x - px) ** 2 + (y - py) ** 2;
			if (d < bestD) {
				bestD = d;
				bx = x;
				by = y;
			}
		}
	}
	if (bx < 0) return null;
	const newTip = [Math.round((bx + 0.5) * scale * 10) / 10, Math.round((by + 0.5) * scale * 10) / 10];
	const tail = coords[0];
	if (Math.hypot(newTip[0] - tail[0], newTip[1] - tail[1]) < 10) return null; // would collapse the shaft
	return {
		...feature,
		geometry: {type: "LineString", coordinates: [...coords.slice(0, -1), newTip]},
		properties: {...(feature.properties || {}), snapped: "junction_tip"},
	};
};

/**
 * DEJ / SEJ: move each vertex of the model's line onto the inner edge of the surface
 * basophilic band (epidermis ↔ dermis interface), keeping the line's order.
 */
const _snapJunction = (feature, raster) => {
	const geom = feature?.geometry;
	if (geom?.type !== "LineString" || !Array.isArray(geom.coordinates)) return null;
	const {w, h, scale} = raster;
	const inner = _junctionEdge(raster);
	if (!inner) return null;
	const R = _junctionRadius(raster);
	// Densify the model line (~3 raster px steps).
	const pts = geom.coordinates.map((c) => [Number(c[0]) / scale, Number(c[1]) / scale]);
	const dense = [];
	for (let i = 0; i + 1 < pts.length; i += 1) {
		const [x0, y0] = pts[i];
		const [x1, y1] = pts[i + 1];
		const steps = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 3));
		for (let k = 0; k < steps; k += 1) dense.push([x0 + ((x1 - x0) * k) / steps, y0 + ((y1 - y0) * k) / steps]);
	}
	if (pts.length) dense.push(pts[pts.length - 1]);
	const out = [];
	let hit = 0;
	for (const [px, py] of dense) {
		const cx = Math.round(px);
		const cy = Math.round(py);
		let bestD = Infinity;
		let bx = -1;
		let by = -1;
		for (let y = Math.max(0, cy - R); y <= Math.min(h - 1, cy + R); y += 1) {
			for (let x = Math.max(0, cx - R); x <= Math.min(w - 1, cx + R); x += 1) {
				if (!inner[y * w + x]) continue;
				const d = (x - px) ** 2 + (y - py) ** 2;
				if (d < bestD) {
					bestD = d;
					bx = x;
					by = y;
				}
			}
		}
		if (bx < 0) continue;
		hit += 1;
		const last = out[out.length - 1];
		if (!last || last[0] !== bx || last[1] !== by) out.push([bx, by]);
	}
	if (hit < dense.length * 0.5 || out.length < 2) return null;
	const simple = _chaikin(_douglasPeucker(out, 1.0), false).map(([x, y]) => [
		Math.round((x + 0.5) * scale * 10) / 10,
		Math.round((y + 0.5) * scale * 10) / 10,
	]);
	if (simple.length < 2) return null;
	return {
		...feature,
		geometry: {type: "LineString", coordinates: simple},
		properties: {...(feature.properties || {}), snapped: "junction"},
	};
};

/** Region outlines (tumour, dermis, inflammation…) should not spill onto glass. */
const NO_CLIP_CLASSES = new Set(["closer_look", "roi", "measurement"]);
const CLIP_MIN_GLASS_FRAC = 0.08;
const CLIP_MIN_KEEP_FRAC = 0.3;

const _clipRegionToTissue = (feature, raster) => {
	const geom = feature?.geometry;
	const p = feature?.properties || {};
	const tool = String(p.tool || "").toLowerCase();
	if (geom?.type !== "Polygon" || tool === "rectangle" || p.clip === false) return feature;
	if (NO_CLIP_CLASSES.has(String(p.class || "").toLowerCase().replace(/_\d+$/, ""))) return feature;
	const fp = _featureFootprint(feature, raster);
	if (!fp) return feature;
	const inter = new Uint8Array(fp.length);
	let fpArea = 0;
	let keep = 0;
	for (let i = 0; i < fp.length; i += 1) {
		if (!fp[i]) continue;
		fpArea += 1;
		if (raster.mask[i]) {
			inter[i] = 1;
			keep += 1;
		}
	}
	if (!fpArea || (fpArea - keep) / fpArea < CLIP_MIN_GLASS_FRAC || keep / fpArea < CLIP_MIN_KEEP_FRAC) {
		return feature;
	}
	const {labels, areas} = _labelComponents(inter, raster.w, raster.h);
	let best = 0;
	for (let l = 1; l < areas.length; l += 1) if (!best || areas[l] > areas[best]) best = l;
	if (!best) return feature;
	const ring = _traceComponent({w: raster.w, h: raster.h, labels}, best);
	if (!ring) return feature;
	const simple = _ringToCanvas(ring, raster);
	if (simple.length < 4) return feature;
	return {
		...feature,
		geometry: {type: "Polygon", coordinates: [simple]},
		properties: {...p, clipped: "tissue"},
	};
};

/** One Chaikin pass: rounds the raster staircase without moving the edge noticeably. */
const _chaikin = (pts, closed) => {
	if (pts.length < 3) return pts;
	const out = closed ? [] : [pts[0]];
	const n = closed ? pts.length : pts.length - 1;
	for (let i = 0; i < n; i += 1) {
		const [ax, ay] = pts[i];
		const [bx, by] = pts[(i + 1) % pts.length];
		out.push([0.75 * ax + 0.25 * bx, 0.75 * ay + 0.25 * by], [0.25 * ax + 0.75 * bx, 0.25 * ay + 0.75 * by]);
	}
	if (!closed) out.push(pts[pts.length - 1]);
	return out;
};

const _ringToCanvas = (ring, raster) => {
	const out = _chaikin(_simplifyRing(ring, MAX_CONTOUR_POINTS >> 1), true).map(([x, y]) => [
		Math.round((x + 0.5) * raster.scale * 10) / 10,
		Math.round((y + 0.5) * raster.scale * 10) / 10,
	]);
	if (out.length >= 3) out.push([...out[0]]);
	return out;
};

/** Per-src raster cache (screenshots are immutable). */
const _rasterCache = new Map();

export const getTissueRaster = (src, canvasSize) => {
	if (!src) return Promise.resolve(null);
	const key = `${src}|${canvasSize?.[0]}x${canvasSize?.[1]}`;
	if (!_rasterCache.has(key)) {
		_rasterCache.set(key, buildTissueRaster(src, canvasSize).catch(() => null));
		if (_rasterCache.size > 12) _rasterCache.delete(_rasterCache.keys().next().value);
	}
	return _rasterCache.get(key);
};
